import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const STORAGE_KEY = 'dopomogai:website-v2:referral-attribution';
const SUPPORTED_PARAMS = [
  'partner_id',
  'partner',
  'ref',
  'referral_code',
  'entry_offer',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

const DEFAULT_ENTRY_OFFER = 'ai_revenue_audit';

type SupportedParam = (typeof SUPPORTED_PARAMS)[number];

export type ReferralAttribution = {
  isReferral: boolean;
  partnerId: string | null;
  partnerRaw: string | null;
  ref: string | null;
  referralCode: string | null;
  entryOffer: string;
  landingPage: string;
  firstReferrerUrl: string | null;
  sessionId: string;
  firstTouchTimestamp: string;
  utm: {
    source: string | null;
    medium: string | null;
    campaign: string | null;
    content: string | null;
    term: string | null;
  };
};

const createSessionId = () =>
  `wsv2_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`;

const parseSearch = (search: string) => {
  const params = new URLSearchParams(search);
  return SUPPORTED_PARAMS.reduce<Record<SupportedParam, string | null>>((acc, key) => {
    acc[key] = params.get(key);
    return acc;
  }, {} as Record<SupportedParam, string | null>);
};

const getStoredAttribution = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ReferralAttribution) : null;
  } catch {
    return null;
  }
};

const persistAttribution = (value: ReferralAttribution) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
};

const hasExplicitReferral = (candidate: Partial<ReferralAttribution>) =>
  Boolean(candidate.partnerId || candidate.partnerRaw || candidate.ref || candidate.referralCode);

const buildCandidate = (pathname: string, search: string): ReferralAttribution => {
  const parsed = parseSearch(search);

  return {
    isReferral: Boolean(parsed.partner_id || parsed.partner || parsed.ref || parsed.referral_code),
    partnerId: parsed.partner_id,
    partnerRaw: parsed.partner,
    ref: parsed.ref,
    referralCode: parsed.referral_code,
    entryOffer: parsed.entry_offer || DEFAULT_ENTRY_OFFER,
    landingPage: pathname,
    firstReferrerUrl: typeof document !== 'undefined' ? document.referrer || null : null,
    sessionId: createSessionId(),
    firstTouchTimestamp: new Date().toISOString(),
    utm: {
      source: parsed.utm_source,
      medium: parsed.utm_medium,
      campaign: parsed.utm_campaign,
      content: parsed.utm_content,
      term: parsed.utm_term,
    },
  };
};

const chooseAttribution = (current: ReferralAttribution | null, candidate: ReferralAttribution) => {
  if (!current) return candidate;
  if (hasExplicitReferral(current)) return current;
  if (hasExplicitReferral(candidate)) {
    return {
      ...candidate,
      sessionId: current.sessionId,
      firstTouchTimestamp: current.firstTouchTimestamp,
      firstReferrerUrl: current.firstReferrerUrl,
      landingPage: current.landingPage,
    };
  }
  return current;
};

export const useReferralAttribution = () => {
  const location = useLocation();
  const [attribution, setAttribution] = useState<ReferralAttribution | null>(() => getStoredAttribution());

  useEffect(() => {
    const candidate = buildCandidate(location.pathname, location.search);
    const resolved = chooseAttribution(getStoredAttribution(), candidate);
    persistAttribution(resolved);
    setAttribution(resolved);
  }, [location.pathname, location.search]);

  const value = useMemo<ReferralAttribution>(() => {
    if (attribution) return attribution;
    return buildCandidate(location.pathname, location.search);
  }, [attribution, location.pathname, location.search]);

  return {
    attribution: value,
    isReferral: value.isReferral,
    hasHighConfidenceReferral: hasExplicitReferral(value),
    showFallbackReferralField: !value.isReferral,
  };
};

export const getLeadSourceDetails = (attribution: ReferralAttribution, typedReferralName?: string) => {
  if (attribution.isReferral) {
    return {
      lead_source: 'referral',
      lead_source_detail: attribution.partnerId || attribution.referralCode || attribution.ref ? 'partner_link' : 'partner_intro',
      referral_partner_id: attribution.partnerId,
      partner_name_raw: attribution.partnerRaw || typedReferralName || null,
      referral_code: attribution.referralCode,
    };
  }

  if (typedReferralName) {
    return {
      lead_source: 'referral',
      lead_source_detail: 'self_reported_referral',
      referral_partner_id: null,
      partner_name_raw: typedReferralName,
      referral_code: null,
    };
  }

  const referrer = attribution.firstReferrerUrl?.toLowerCase() || '';
  const isSearch = /(google|bing|duckduckgo|search|yahoo)/.test(referrer);

  return {
    lead_source: isSearch ? 'organic' : 'direct',
    lead_source_detail: 'website_inbound',
    referral_partner_id: null,
    partner_name_raw: null,
    referral_code: null,
  };
};
